import { useEffect, useState } from 'react';
import type { CostCategory, CostItem, CostStatus, Document } from 'used-vehicle-contracts';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { Textarea } from '../../components/ui/Textarea';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { toOptionalNumber, toOptionalText } from '../estimates/estimates-display';
import { COST_CATEGORIES, EDITABLE_COST_STATUSES } from './costs-display';

/** What the sheet hands back: a manual creation, or a patch of an existing row. */
export type CostItemSubmission =
  | { kind: 'MANUAL'; category: CostCategory; name: string; estimate: number; reason: string }
  | {
      kind: 'PATCH';
      id: string;
      estimate: number | null;
      actualToDate: number | null;
      remainingEstimate: number | null;
      status: Exclude<CostStatus, 'WAIVED'>;
      supplier: string | null;
      workDate: string | null;
      /** The INVOICE document that proves the actual, when one is on file (plan §8; PP §8). */
      invoiceDocumentId: string | null;
      notes: string | null;
    };

export interface CostItemSheetProps {
  isOpen: boolean;
  /** `null` opens the manual-cost form; an item opens the edit form. */
  item: CostItem | null;
  /**
   * The vehicle's INVOICE documents. Empty on an estimate — documents attach to stock vehicles only
   * (plan §11 item 5) — which is why the Select is simply not rendered there.
   */
  invoiceDocuments?: readonly Document[];
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (submission: CostItemSubmission) => void;
}

interface FormState {
  category: CostCategory;
  name: string;
  estimate: string;
  actualToDate: string;
  remainingEstimate: string;
  status: Exclude<CostStatus, 'WAIVED'>;
  supplier: string;
  workDate: string;
  invoiceDocumentId: string;
  notes: string;
}

const EMPTY: FormState = {
  category: 'RECONDITIONING',
  name: '',
  estimate: '',
  actualToDate: '',
  remainingEstimate: '',
  status: 'ESTIMATED',
  supplier: '',
  workDate: '',
  invoiceDocumentId: '',
  notes: '',
};

const fromItem = (item: CostItem | null): FormState =>
  item === null
    ? { ...EMPTY }
    : {
        category: item.category,
        name: item.name,
        estimate: item.estimate === null ? '' : String(item.estimate),
        actualToDate: item.actualToDate === null ? '' : String(item.actualToDate),
        remainingEstimate: item.remainingEstimate === null ? '' : String(item.remainingEstimate),
        status: item.status === 'WAIVED' ? 'ESTIMATED' : item.status,
        supplier: item.supplier ?? '',
        workDate: item.workDate ?? '',
        invoiceDocumentId: item.invoiceDocumentId ?? '',
        notes: item.notes ?? '',
      };

/**
 * One cost item, as a person edits it (plan §7 step 2, §7 costs tab).
 *
 * The three amounts live on the same form on purpose: what the work was expected to cost, what has been
 * invoiced so far, and what is still to come. The **status** decides which of them the projection reads
 * (plan §5.5), which is why it sits between them rather than at the top — and why a finished job asks for
 * its actual before it can be saved.
 */
export const CostItemSheet = ({ isOpen, item, invoiceDocuments = [], isSaving, error, onClose, onSubmit }: CostItemSheetProps) => {
  const { t } = useI18nTranslation();
  const [form, setForm] = useState<FormState>(() => fromItem(item));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(fromItem(item));
      setTouched(false);
    }
  }, [isOpen, item]);

  const isManual = item === null;
  const change = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }));

  const nameMissing = isManual && form.name.trim() === '';
  const estimateMissing = isManual && toOptionalNumber(form.estimate) === null;
  const reasonMissing = isManual && form.notes.trim() === '';
  const actualMissing = !isManual && form.status === 'DONE' && toOptionalNumber(form.actualToDate) === null;
  const canSubmit = !nameMissing && !estimateMissing && !reasonMissing && !actualMissing;

  const submit = () => {
    setTouched(true);

    if (!canSubmit) {
      return;
    }

    if (isManual) {
      onSubmit({
        kind: 'MANUAL',
        category: form.category,
        name: form.name.trim(),
        estimate: toOptionalNumber(form.estimate) ?? 0,
        reason: form.notes.trim(),
      });

      return;
    }

    onSubmit({
      kind: 'PATCH',
      id: item.id,
      estimate: toOptionalNumber(form.estimate),
      actualToDate: toOptionalNumber(form.actualToDate),
      remainingEstimate: toOptionalNumber(form.remainingEstimate),
      status: form.status,
      supplier: toOptionalText(form.supplier),
      workDate: toOptionalText(form.workDate),
      invoiceDocumentId: form.invoiceDocumentId === '' ? null : form.invoiceDocumentId,
      notes: toOptionalText(form.notes),
    });
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={isManual ? t('costs.manualTitle') : item.name}
      description={isManual ? t('costs.manualBody') : t('costs.editBody')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={isSaving}
            onClick={submit}
          >
            {t('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        {isManual ? (
          <>
            <FormField
              id="cost-category"
              label={t('costs.category')}
              hint={t('costs.categoryHint')}
            >
              <Select
                id="cost-category"
                value={form.category}
                onChange={(event) => change({ category: event.target.value as CostCategory })}
                options={COST_CATEGORIES.map((category) => ({ value: category, label: t(`enums.costCategory.${category}`) }))}
              />
            </FormField>

            <FormField
              id="cost-name"
              label={t('costs.name')}
              error={touched && nameMissing ? t('costs.nameRequired') : undefined}
            >
              <Input
                id="cost-name"
                value={form.name}
                hasError={touched && nameMissing}
                onChange={(event) => change({ name: event.target.value })}
              />
            </FormField>
          </>
        ) : null}

        <FormField
          id="cost-estimate"
          label={t('costs.estimate')}
          hint={isManual ? undefined : t('costs.estimateHint')}
          error={touched && estimateMissing ? t('costs.estimateRequired') : undefined}
        >
          <Input
            id="cost-estimate"
            type="number"
            min={0}
            step={0.001}
            value={form.estimate}
            hasError={touched && estimateMissing}
            onChange={(event) => change({ estimate: event.target.value })}
          />
        </FormField>

        {isManual ? (
          <FormField
            id="cost-reason"
            label={t('costs.reason')}
            hint={t('costs.reasonHint')}
            error={touched && reasonMissing ? t('costs.reasonRequired') : undefined}
          >
            <Textarea
              id="cost-reason"
              rows={3}
              value={form.notes}
              hasError={touched && reasonMissing}
              onChange={(event) => change({ notes: event.target.value })}
            />
          </FormField>
        ) : (
          <>
            <FormField
              id="cost-status"
              label={t('costs.status')}
              hint={t('costs.statusHint')}
            >
              <Select
                id="cost-status"
                value={form.status}
                onChange={(event) => change({ status: event.target.value as Exclude<CostStatus, 'WAIVED'> })}
                options={EDITABLE_COST_STATUSES.map((status) => ({ value: status, label: t(`enums.costStatus.${status}`) }))}
              />
            </FormField>

            <FormField
              id="cost-actual"
              label={t('costs.actualToDate')}
              hint={t('costs.actualHint')}
              error={touched && actualMissing ? t('costs.actualRequired') : undefined}
            >
              <Input
                id="cost-actual"
                type="number"
                min={0}
                step={0.001}
                value={form.actualToDate}
                hasError={touched && actualMissing}
                onChange={(event) => change({ actualToDate: event.target.value })}
              />
            </FormField>

            <FormField
              id="cost-remaining"
              label={t('costs.remainingEstimate')}
              hint={t('costs.remainingHint')}
            >
              <Input
                id="cost-remaining"
                type="number"
                min={0}
                step={0.001}
                value={form.remainingEstimate}
                onChange={(event) => change({ remainingEstimate: event.target.value })}
              />
            </FormField>

            <FormField
              id="cost-supplier"
              label={t('costs.supplier')}
            >
              <Input
                id="cost-supplier"
                value={form.supplier}
                onChange={(event) => change({ supplier: event.target.value })}
              />
            </FormField>

            <FormField
              id="cost-work-date"
              label={t('costs.workDate')}
            >
              <Input
                id="cost-work-date"
                type="date"
                value={form.workDate}
                onChange={(event) => change({ workDate: event.target.value })}
              />
            </FormField>

            {invoiceDocuments.length === 0 ? null : (
              <FormField
                id="cost-invoice"
                label={t('costs.invoice')}
                hint={t('costs.invoiceHint')}
              >
                <Select
                  id="cost-invoice"
                  value={form.invoiceDocumentId}
                  placeholder={t('costs.noInvoice')}
                  onChange={(event) => change({ invoiceDocumentId: event.target.value })}
                  options={invoiceDocuments.map((document) => ({ value: document.id, label: document.title }))}
                />
              </FormField>
            )}

            <FormField
              id="cost-notes"
              label={t('costs.notes')}
            >
              <Textarea
                id="cost-notes"
                rows={3}
                value={form.notes}
                onChange={(event) => change({ notes: event.target.value })}
              />
            </FormField>
          </>
        )}
      </div>
    </Sheet>
  );
};
